import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/generals/page-hero'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { SectionHeading } from '@/components/generals/section-heading'
import { PriceEstimator } from '@/components/widgets/price-estimator'
import { FinancingSection } from '@/components/landing/sections/financing-section'
import { GuaranteesSection } from '@/components/landing/sections/guarantees-section'
import { FeeGrid, PriceList, StartingPrices } from '@/components/landing/pricing/price-sections'
import { PRICE_FINEPRINT } from '@/constants/pricing'
import { siteConfig, QUOTE_PATH } from '@/constants/site'

const PricingPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Pricing'
                description="What garage door work usually costs in the Riverton metro: springs, cables, openers and new doors, plus every fee and financing option."
            />

            <PageHero
                eyebrow="Prices"
                title="Our usual prices, in plain sight."
                accent={['plain', 'sight.']}
                description="Most door companies keep their prices to themselves. We publish ours: a fair range up front spares you a phone call and spares us a wasted trip."
                actions={(
                    <>
                        <ButtonLink href='#estimator' size='lg'>Estimate my job</ButtonLink>
                        <ButtonLink href={siteConfig.phoneHref} variant='outline' size='lg' icon='phone'>Call {siteConfig.phone}</ButtonLink>
                    </>
                )}
                aside={<StartingPrices />}
            />

            <section id='estimator' className='scroll-mt-24 pb-24 pt-8 md:pb-32'>
                <Container>
                    <SectionHeading
                        eyebrow="Estimate"
                        title="Choose the work. See what it usually costs."
                        description="Select each repair or part you think the door needs. We total the typical range for homes around Riverton, and you can pass it straight on as a quote request."
                    />

                    <div className='mt-14'>
                        <PriceEstimator />
                    </div>
                </Container>
            </section>

            <section id='price-list' className='bg-paper py-24 md:py-32'>
                <Container>
                    <SectionHeading
                        eyebrow="Price list"
                        title="Everything we charge for, in one list."
                        description={PRICE_FINEPRINT}
                    />

                    <div className='mt-14'>
                        <PriceList />
                    </div>
                </Container>
            </section>

            <section id='fees' className='py-24 md:py-32'>
                <Container>
                    <SectionHeading
                        eyebrow="Extra charges"
                        title="Every fee, stated up front."
                        description="Here is each fee a visit can carry, so you see it before you book. We mention them on the phone, never for the first time on the invoice."
                    />

                    <div className='mt-14'>
                        <FeeGrid />
                    </div>
                </Container>
            </section>

            <FinancingSection
                title="Pay over 12–60 months."
                description="Available on new doors, openers and bigger repairs through a third-party lender, with 0% APR for 12 months on approved credit."
                showSteps
            />

            <GuaranteesSection />

            <CtaBand
                title="Ready for a price on your own door?"
                body="Share a few details, and a photo if you have one. We'll text back a price range, usually within 30 minutes."
                primary={{ label: "Request a price", href: QUOTE_PATH }}
            />
        </Layout>
    )
}

export default PricingPage
